import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiService, downloadBlob } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Download,
  Filter,
  ArrowUpDown,
  Loader2,
  Users,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Clock,
  MoreVertical,
  Archive,
  ArchiveRestore,
  Trash2,
  Eye
} from 'lucide-react';
import { SUPPORT_AREAS, normalizeSupportArea } from '../constants/supportAreas';

const GRADES = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const TIERS = ['Tier 1', 'Tier 2', 'Tier 3'];
const STATUSES = ['New Student', 'Improving', 'Limited Progress', 'Needs Review'];
const REVIEW_STATUSES = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'review_soon', label: 'Review Soon' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'no_review_date', label: 'No Review Date' }
];
const ACTIVE_FILTERS = [
  { value: 'active', label: 'Active Students' },
  { value: 'archived', label: 'Archived Students' },
  { value: 'all', label: 'All Students' }
];

export default function StudentsPage() {
  const navigate = useNavigate();
  const { canEdit, selectedSchoolId, isSuperAdmin, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [gradeFilter, setGradeFilter] = useState(searchParams.get('grade') || 'all');
  const [tierFilter, setTierFilter] = useState(searchParams.get('tier') || 'all');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [supportAreaFilter, setSupportAreaFilter] = useState(searchParams.get('support_area') || 'all');
  const [reviewStatusFilter, setReviewStatusFilter] = useState(searchParams.get('review_status') || 'all');
  const [activeFilter, setActiveFilter] = useState('active');

  useEffect(() => {
    fetchStudents();
  }, [gradeFilter, tierFilter, statusFilter, supportAreaFilter, reviewStatusFilter, activeFilter, selectedSchoolId]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const params = {};
      
      // Add school filter for super admin
      if (selectedSchoolId) {
        params.school_id = selectedSchoolId;
      }
      
      // Handle active filter
      if (activeFilter === 'active') {
        params.active = true;
      } else if (activeFilter === 'archived') {
        params.active = false;
      }
      // 'all' doesn't set active parameter
      
      if (search) params.search = search;
      if (gradeFilter && gradeFilter !== 'all') params.grade = gradeFilter;
      if (tierFilter && tierFilter !== 'all') params.tier = tierFilter;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (supportAreaFilter && supportAreaFilter !== 'all') params.concern_area = supportAreaFilter;
      if (reviewStatusFilter && reviewStatusFilter !== 'all') params.review_status = reviewStatusFilter;

      const response = await apiService.getStudents(params);
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      // More detailed error handling
      if (error.response) {
        console.error('Response error:', error.response.status, error.response.data);
        if (error.response.status === 401) {
          toast.error('Session expired. Please log in again.');
        } else if (error.response.status === 403) {
          toast.error('Access denied. You may not have permission to view students.');
        } else {
          toast.error(`Failed to load students: ${error.response.data?.detail || 'Server error'}`);
        }
      } else if (error.request) {
        console.error('Network error - no response received');
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('Failed to load students');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveStudent = async (studentId, studentName, archive = true) => {
    setActionLoading(studentId);
    try {
      await apiService.updateStudent(studentId, { active: !archive });
      toast.success(`${studentName} ${archive ? 'archived' : 'restored'} successfully`);
      fetchStudents();
    } catch (error) {
      console.error('Failed to update student:', error);
      toast.error(`Failed to ${archive ? 'archive' : 'restore'} student`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteStudent = async (studentId, studentName) => {
    setActionLoading(studentId);
    try {
      await apiService.deleteStudent(studentId);
      toast.success(`${studentName} permanently deleted`);
      setDeleteConfirm(null);
      fetchStudents();
    } catch (error) {
      console.error('Failed to delete student:', error);
      toast.error('Failed to delete student');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchStudents();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (gradeFilter && gradeFilter !== 'all') params.grade = gradeFilter;
      if (tierFilter && tierFilter !== 'all') params.tier = tierFilter;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;

      const response = await apiService.exportStudents(params);
      const date = new Date().toISOString().split('T')[0];
      downloadBlob(response.data, `mtss_students_${date}.csv`);
      toast.success('Export downloaded successfully');
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error('Failed to export students');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setGradeFilter('all');
    setTierFilter('all');
    setStatusFilter('all');
    setSupportAreaFilter('all');
    setReviewStatusFilter('all');
    setSearchParams({});
  };

  const getTierBadgeClass = (tier) => {
    switch (tier) {
      case 'Tier 1': return 'badge-tier1';
      case 'Tier 2': return 'badge-tier2';
      case 'Tier 3': return 'badge-tier3';
      default: return '';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'New Student': return 'badge-new';
      case 'Improving': return 'badge-improving';
      case 'Limited Progress': return 'badge-limited';
      case 'Needs Review': return 'badge-review';
      default: return '';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'declining': return <TrendingDown className="h-4 w-4 text-red-600" />;
      case 'flat': return <Minus className="h-4 w-4 text-amber-600" />;
      default: return <span className="text-[#A3A3A3] text-xs">—</span>;
    }
  };

  const getReviewBadge = (reviewStatus) => {
    switch (reviewStatus) {
      case 'overdue':
        return (
          <span className="inline-flex items-center gap-1 text-red-600" title="Overdue">
            <AlertTriangle className="h-3.5 w-3.5" />
          </span>
        );
      case 'review_soon':
        return (
          <span className="inline-flex items-center gap-1 text-amber-600" title="Review Soon">
            <Clock className="h-3.5 w-3.5" />
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="page-container" data-testid="students-page">
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title">Students</h1>
          <p className="page-subtitle">Manage and track student progress</p>
        </div>
        <div className="flex gap-3">
          {canEdit && (
            <>
              <Button 
                variant="outline" 
                onClick={handleExport} 
                disabled={exporting}
                data-testid="export-students-btn"
                className="border-[#E5E5E5] rounded-xl hover:border-[#2C4C3B]"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export CSV
              </Button>
              <Button 
                className="bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl" 
                data-testid="add-student-btn"
                onClick={() => navigate(selectedSchoolId ? `/students/new?school_id=${selectedSchoolId}` : '/students/new')}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Student
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card mb-6">
        <div className="card-content">
          {/* Demo Toggle */}
          <form onSubmit={handleSearch} className="mb-4">
            <div className="search-container" style={{ maxWidth: '400px' }}>
              <Search className="search-icon" size={18} />
              <Input
                type="text"
                placeholder="Search by name, teacher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 rounded-xl border-[#E5E5E5] focus:border-[#2C4C3B]"
                data-testid="search-input"
              />
            </div>
          </form>

          <div className="filters-bar">
            <Filter className="h-4 w-4 text-[#595959]" />
            
            <Select value={activeFilter} onValueChange={setActiveFilter}>
              <SelectTrigger className="w-[160px] rounded-xl border-[#E5E5E5]" data-testid="active-filter">
                <SelectValue placeholder="Show" />
              </SelectTrigger>
              <SelectContent>
                {ACTIVE_FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={gradeFilter} onValueChange={setGradeFilter}>
              <SelectTrigger className="w-[130px] rounded-xl border-[#E5E5E5]" data-testid="grade-filter">
                <SelectValue placeholder="Grade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Grades</SelectItem>
                {GRADES.map((grade) => (
                  <SelectItem key={grade} value={grade}>Grade {grade}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className="w-[130px] rounded-xl border-[#E5E5E5]" data-testid="tier-filter">
                <SelectValue placeholder="Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                {TIERS.map((tier) => (
                  <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] rounded-xl border-[#E5E5E5]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={supportAreaFilter} onValueChange={setSupportAreaFilter}>
              <SelectTrigger className="w-[180px] rounded-xl border-[#E5E5E5]" data-testid="support-area-filter">
                <SelectValue placeholder="Support Area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Support Areas</SelectItem>
                {SUPPORT_AREAS.map((area) => (
                  <SelectItem key={area} value={area}>{area}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={reviewStatusFilter} onValueChange={setReviewStatusFilter}>
              <SelectTrigger className="w-[150px] rounded-xl border-[#E5E5E5]" data-testid="review-status-filter">
                <SelectValue placeholder="Review Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reviews</SelectItem>
                {REVIEW_STATUSES.map((rs) => (
                  <SelectItem key={rs.value} value={rs.value}>{rs.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn" className="text-[#595959] hover:text-[#1C1C1C]">
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Students Table */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B]" />
          </div>
        ) : students.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <Users size={32} />
            </div>
            <h3 className="empty-title" data-testid="students-empty-title">
              {!isAdmin && !(search || gradeFilter !== 'all' || tierFilter !== 'all' || statusFilter !== 'all' || supportAreaFilter !== 'all') && activeFilter !== 'archived'
                ? 'No assigned students found'
                : 'No students found'}
            </h3>
            <p className="empty-text" data-testid="students-empty-text">
              {search || gradeFilter !== 'all' || tierFilter !== 'all' || statusFilter !== 'all' || supportAreaFilter !== 'all'
                ? 'Try adjusting your filters'
                : activeFilter === 'archived'
                  ? 'No archived students. Students you archive will appear here.'
                  : isSuperAdmin && !selectedSchoolId
                      ? 'Select a school from the dropdown above to view or add students'
                      : !isAdmin
                        ? 'No students are currently assigned to your account. Contact your school administrator if you believe this is an error.'
                        : 'Add your first student to get started'}
            </p>
            {canEdit && isAdmin && activeFilter !== 'archived' && (
              <Button 
                className="bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl"
                onClick={() => navigate(selectedSchoolId ? `/students/new?school_id=${selectedSchoolId}` : '/students/new')}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Student
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="students-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Grade</th>
                  <th>Teacher</th>
                  <th>Support Area</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th>Trend</th>
                  <th>Latest Score</th>
                  <th>Review Date</th>
                  {canEdit && <th className="text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {students.map((student) => (
                  <tr
                    key={student.id}
                    className={`${!student.active ? 'opacity-60 bg-[#F3F4F1]' : ''}`}
                    data-testid={`student-row-${student.id}`}
                  >
                    <td 
                      onClick={() => navigate(`/students/${student.id}`)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="student-avatar" style={{ width: 36, height: 36, fontSize: 14 }}>
                          {student.full_name?.charAt(0) || 'S'}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{student.full_name}</span>
                          {!student.active && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-lg text-xs font-medium bg-[#F3F4F1] text-[#595959]">
                              Archived
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td onClick={() => navigate(`/students/${student.id}`)} className="cursor-pointer">
                      {student.grade}
                    </td>
                    <td onClick={() => navigate(`/students/${student.id}`)} className="cursor-pointer">
                      {student.teacher_name || <span className="text-[#A3A3A3]">—</span>}
                    </td>
                    <td onClick={() => navigate(`/students/${student.id}`)} className="cursor-pointer">
                      {student.concern_area && student.concern_area.length > 0 ? (
                        <span className="text-[#1C1C1C]">
                          {student.concern_area.length <= 2 
                            ? student.concern_area.map(a => normalizeSupportArea(a)).join(', ')
                            : `${student.concern_area.slice(0, 2).map(a => normalizeSupportArea(a)).join(', ')} +${student.concern_area.length - 2} more`
                          }
                        </span>
                      ) : (
                        <span className="text-[#A3A3A3]">—</span>
                      )}
                    </td>
                    <td onClick={() => navigate(`/students/${student.id}`)} className="cursor-pointer">
                      <span className={`badge ${getTierBadgeClass(student.current_tier)}`}>
                        {student.current_tier}
                      </span>
                    </td>
                    <td onClick={() => navigate(`/students/${student.id}`)} className="cursor-pointer">
                      <span className={`badge ${getStatusBadgeClass(student.status)}`}>
                        {student.status}
                      </span>
                    </td>
                    <td onClick={() => navigate(`/students/${student.id}`)} className="cursor-pointer">
                      <div className="flex items-center justify-center">
                        {getTrendIcon(student.trend)}
                      </div>
                    </td>
                    <td onClick={() => navigate(`/students/${student.id}`)} className="cursor-pointer">
                      {student.latest_score !== null
                        ? student.latest_score
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td onClick={() => navigate(`/students/${student.id}`)} className="cursor-pointer">
                      <div className="flex items-center gap-2">
                        {student.review_date || <span className="text-slate-400">—</span>}
                        {getReviewBadge(student.review_status)}
                      </div>
                    </td>
                    {canEdit && (
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0"
                              data-testid={`actions-btn-${student.id}`}
                            >
                              {actionLoading === student.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <MoreVertical className="h-4 w-4" />
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/students/${student.id}`)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Profile
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {student.active ? (
                              <DropdownMenuItem 
                                onClick={() => handleArchiveStudent(student.id, student.full_name, true)}
                              >
                                <Archive className="h-4 w-4 mr-2" />
                                Archive Student
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                onClick={() => handleArchiveStudent(student.id, student.full_name, false)}
                              >
                                <ArchiveRestore className="h-4 w-4 mr-2" />
                                Restore Student
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setDeleteConfirm(student)}
                              className="text-red-600 focus:text-red-600 focus:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Permanently
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-full bg-red-100">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Delete Student?</h3>
                  <p className="text-sm text-slate-500">This action cannot be undone</p>
                </div>
              </div>
              
              <p className="text-slate-600 mb-4">
                Are you sure you want to permanently delete <strong>{deleteConfirm.full_name}</strong>? 
                This will remove all their interventions, progress data, and reports.
              </p>
              
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteStudent(deleteConfirm.id, deleteConfirm.full_name)}
                  disabled={actionLoading === deleteConfirm.id}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-btn"
                >
                  {actionLoading === deleteConfirm.id ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Delete Permanently
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
